/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import React, { useMemo } from 'react';
import {
  SidebarItem,
  SidebarSubmenu,
  SidebarSubmenuItem,
} from '@backstage/core-components';
import {
  catalogApiRef,
  entityPresentationApiRef,
  entityRouteRef,
} from '@backstage/plugin-catalog-react';
import {
  identityApiRef,
  useApi,
  useRouteRef,
} from '@backstage/core-plugin-api';
import {
  DEFAULT_NAMESPACE,
  type Entity,
  getCompoundEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import useAsync from 'react-use/lib/useAsync';
import { Typography, makeStyles } from '@material-ui/core';
import { TeamAvatar } from '../home/TeamAvatar';
import { useViewAs } from '../viewAs/ViewAsContext';
import GroupIcon from '@material-ui/icons/People';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

/**
 * Drop-in replacement for @backstage/plugin-org's MyGroupsSidebarItem that
 * (a) renders each team's GitHub avatar (from spec.profile.picture, set by
 * the github-org provider) instead of the same generic People icon on every
 * row, and (b) lists EVERY team in the org, split into "Member" (yours) and
 * "Others" -- full who-is-where visibility instead of a my-teams-only view.
 * Falls back to deterministic initials when no avatar URL is present
 * (file-managed groups, or groups whose picture failed to load).
 */

const teamSlug = (e: Entity): string => e.metadata.name;
const teamPicture = (e: Entity): string | undefined =>
  (e.spec?.profile as { picture?: string } | undefined)?.picture;

// Member/other partition over ONE all-groups query (relations carry
// hasMember). Exported for unit tests.
export const partitionGroups = (
  groups: Entity[],
  userRef: string,
): { member: Entity[]; others: Entity[] } => {
  const member: Entity[] = [];
  const others: Entity[] = [];
  for (const g of groups) {
    const mine = (g.relations ?? []).some(
      rel => rel.type === 'hasMember' && rel.targetRef === userRef,
    );
    (mine ? member : others).push(g);
  }
  return { member, others };
};

const useSectionStyles = makeStyles(theme => ({
  sectionLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    padding: theme.spacing(1.5, 2, 0.5),
  },
  // Quiet, non-interactive advisory shown when the viewed user has no teams.
  advisory: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    lineHeight: 1.4,
    padding: theme.spacing(1.5, 2),
  },
  advisoryIcon: {
    fontSize: 18,
    flexShrink: 0,
    marginTop: 1,
    color: theme.palette.text.hint,
  },
}));

// Per-team icon component built ONCE per (slug, pictureUrl) via useMemo, so
// React doesn't remount the avatar on every parent render. Lives inside a
// child component so the hook stays at component top level.
const TeamSubmenuItem = ({
  entity,
  to,
  title,
  subtitle,
}: {
  entity: Entity;
  to: string;
  title: string;
  subtitle?: string;
}) => {
  const slug = teamSlug(entity);
  const picture = teamPicture(entity);
  const Icon = useMemo(
    () =>
      function TeamIcon() {
        return <TeamAvatar slug={slug} pictureUrl={picture} size={20} />;
      },
    [slug, picture],
  );
  return (
    <SidebarSubmenuItem title={title} subtitle={subtitle} to={to} icon={Icon} />
  );
};

export const MyTeamsSidebarItem = ({
  singularTitle,
  pluralTitle,
  filter,
}: {
  singularTitle: string;
  pluralTitle: string;
  filter?: Record<string, string | symbol | (string | symbol)[]>;
}) => {
  const identityApi = useApi(identityApiRef);
  const catalogApi = useApi(catalogApiRef);
  const entityRoute = useRouteRef(entityRouteRef);
  const presentationApi = useApi(entityPresentationApiRef);
  const classes = useSectionStyles();
  // Under the "View as" lens, partition "Member vs Others" by the VIEWED
  // user's ref, so "Member" reflects the teams that user belongs to.
  const { viewingAs, viewAsRef, isReadOnly } = useViewAs();

  const { value } = useAsync(async () => {
    const profile = await identityApi.getBackstageIdentity();
    // ONE query for every group; membership derives from each group's
    // hasMember relations client-side (no second round-trip).
    const response = await catalogApi.getEntities({
      filter: [{ kind: 'group', ...(filter ?? {}) }],
      fields: ['metadata', 'kind', 'spec.profile', 'relations'],
    });
    const userRef =
      viewingAs && viewAsRef ? viewAsRef : profile.userEntityRef;
    return { groups: response.items, userRef };
  }, [viewingAs, viewAsRef]);

  const sections = useMemo(() => {
    if (!value) return null;
    const byTitle = (a: Entity, b: Entity) =>
      presentationApi
        .forEntity(a)
        .snapshot.primaryTitle.localeCompare(
          presentationApi.forEntity(b).snapshot.primaryTitle,
        );
    const { member, others } = partitionGroups(value.groups, value.userRef);
    return { member: member.sort(byTitle), others: others.sort(byTitle) };
  }, [value, presentationApi]);

  if (!sections || sections.member.length + sections.others.length === 0) {
    return null;
  }

  // Under the lens, a viewed user who belongs to no teams gets an advisory
  // instead of the full org "Others" list -- faithful to what they'd see, and
  // it explains the empty membership rather than implying broad access.
  if (viewingAs && sections.member.length === 0) {
    return (
      <SidebarItem icon={GroupIcon} text={pluralTitle}>
        <SidebarSubmenu title={pluralTitle}>
          <Typography component="div" className={classes.advisory}>
            <InfoOutlinedIcon className={classes.advisoryIcon} />
            {isReadOnly
              ? 'Read-only users belong to no teams.'
              : 'This user belongs to no teams.'}
          </Typography>
        </SidebarSubmenu>
      </SidebarItem>
    );
  }

  // One group total in the whole org: a submenu with sections is ceremony.
  if (sections.member.length + sections.others.length === 1) {
    const group = sections.member[0] ?? sections.others[0];
    const picture = teamPicture(group);
    const Icon = function SingleTeamIcon() {
      return (
        <TeamAvatar slug={teamSlug(group)} pictureUrl={picture} size={20} />
      );
    };
    return (
      <SidebarItem
        text={singularTitle}
        to={entityRoute(getCompoundEntityRef(group))!}
        icon={Icon}
      />
    );
  }

  const renderTeam = (group: Entity) => {
    const display = presentationApi.forEntity(group);
    return (
      <TeamSubmenuItem
        key={stringifyEntityRef(group)}
        entity={group}
        to={entityRoute(getCompoundEntityRef(group))!}
        title={display.snapshot.primaryTitle}
        subtitle={
          group.metadata.namespace !== DEFAULT_NAMESPACE
            ? group.metadata.namespace
            : undefined
        }
      />
    );
  };

  return (
    <SidebarItem icon={GroupIcon} text={pluralTitle}>
      <SidebarSubmenu title={pluralTitle}>
        {sections.member.length > 0 && (
          <Typography component="div" className={classes.sectionLabel}>
            Member
          </Typography>
        )}
        {sections.member.map(renderTeam)}
        {sections.others.length > 0 && (
          <Typography component="div" className={classes.sectionLabel}>
            Others
          </Typography>
        )}
        {sections.others.map(renderTeam)}
      </SidebarSubmenu>
    </SidebarItem>
  );
};
